import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from './TopBar';
import ConfirmModal from './ConfirmModal';
import './CartPage.css';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fetchAndValidateCartItems, updateCartItemQuantity, removeFromCart } from '../services/cartService';

export default function CartPage() {
  const { user, cartRefreshTrigger } = useAuth();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [cartItems, setCartItems] = useState([]);
  const [loadingCart, setLoadingCart] = useState(false);
  const [cartError, setCartError] = useState(null);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);

  // Fetch cart items from database
  useEffect(() => {
    const fetchCartItems = async () => {
      if (!user) {
        setCartItems([]);
        return;
      }

      try {
        setLoadingCart(true);
        setCartError(null);

        const { items, removedCount } = await fetchAndValidateCartItems(user.$id);

        if (removedCount > 0) {
          showError(`${removedCount} item(s) removed as they are no longer available.`);
        }

        setCartItems(items);
      } catch (error) {
        console.error('Error fetching cart items:', error);
        setCartError('Failed to load cart items');
      } finally {
        setLoadingCart(false);
      }
    };

    fetchCartItems();
  }, [user, cartRefreshTrigger]);

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) {
      // Show confirmation modal instead of browser confirm
      setItemToRemove(itemId);
      setShowConfirmModal(true);
      return;
    }

    try {
      await updateCartItemQuantity(itemId, newQuantity);

      // Update local state
      setCartItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      ));
    } catch (error) {
      console.error('Error updating quantity:', error);
      showError(error, 'Failed to update quantity. Please try again.');
    }
  };

  const handleDeleteClick = (itemId) => {
    setItemToRemove(itemId);
    setShowConfirmModal(true);
  };

  const confirmRemove = async () => {
    if (itemToRemove) {
      await removeItem(itemToRemove);
      setShowConfirmModal(false);
      setItemToRemove(null);
    }
  };

  const cancelRemove = () => {
    setShowConfirmModal(false);
    setItemToRemove(null);
  };

  const removeItem = async (itemId) => {
    try {
      await removeFromCart(itemId);

      // Update local state
      setCartItems(prev => prev.filter(item => item.id !== itemId));
      showSuccess('Item removed from cart');
    } catch (error) {
      console.error('Error removing item:', error);
      showError(error, 'Failed to remove item. Please try again.');
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  return (
    <div className="cart-page">
      <TopBar />
      <main className="cart-main">
        <div className="cart-content">
          <h1 className="cart-page-title">Your Cart</h1>

          {loadingCart ? (
            <div className="cart-loading">
              <div className="cart-spinner"></div>
              <p>Loading cart...</p>
            </div>
          ) : cartError ? (
            <div className="cart-error-message">
              <p>{cartError}</p>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="cart-empty-state">
              <p>Your cart is empty</p>
              <button onClick={() => navigate('/buy-food')} className="browse-food-btn">
                Browse Food
              </button>
            </div>
          ) : (
            <>
              <div className="cart-items-list">
                {cartItems.map((item) => (
                  <div key={item.id} className="cart-page-item">
                    <div className="cart-page-item-image-container">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="cart-page-item-image"
                        />
                      ) : (
                        <div className="cart-page-item-image-placeholder">
                          🍽️
                        </div>
                      )}
                    </div>

                    <div className="cart-page-item-details">
                      <h3 className="cart-page-item-name">{item.name}</h3>
                      <p className="cart-page-item-vendor">Prepared by {item.vendor}</p>
                      <p className="cart-page-item-time">Order by: {item.orderBy}</p>
                      <p className="cart-page-item-time">Pickup by: {item.pickupBy}</p>
                      <p className="cart-page-item-pickup"><strong>Pickup:</strong> {item.pickupLocation}</p>

                      <div className="cart-page-item-price">RM {item.price}</div>

                      <div className="cart-page-quantity-controls">
                        <button
                          className="cart-page-quantity-btn"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        >
                          −
                        </button>
                        <span className="cart-page-quantity-value">{item.quantity}</span>
                        <button
                          className="cart-page-quantity-btn"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          +
                        </button>
                      </div>

                      <button
                        className="cart-page-remove-btn"
                        onClick={() => handleDeleteClick(item.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="cart-page-summary">
                <div className="cart-page-subtotal">
                  <span className="subtotal-label">Subtotal</span>
                  <span className="subtotal-amount">RM {calculateSubtotal().toFixed(2)}</span>
                </div>
                <button
                  className="cart-page-checkout-btn"
                  onClick={handleCheckout}
                >
                  Proceed to Checkout
                </button>
              </div>
            </>
          )}
        </div>
      </main>

      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={confirmRemove}
        onCancel={cancelRemove}
        title="Remove Item"
        message="Remove this item from cart?"
      />
    </div>
  );
}

